import { useCallback, useEffect, useRef, useState } from 'react';

import { send } from '@actual-app/core/platform/client/connection';

import { useLocalPref } from '#hooks/useLocalPref';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { registrarUso } from '#uso';

import {
  elegirCarpeta,
  escribirEnCarpeta,
  guardarCopiaInterna,
  leerCarpeta,
  permisoCarpeta,
} from './almacen';
import {
  diaMasReciente,
  estadoAncla,
  leerConfigCopia,
  nombreArchivoAuto,
  tocaCopiaAuto,
} from './automatica';
import type { ConfigCopiaAuto } from './automatica';
import { diaLocal } from './copia';

export type ResultadoAuto = 'hecha' | 'sin-permiso' | 'error';

/** Una sola copia a la vez aunque haya varias pantallas montadas. */
let enCurso: Promise<ResultadoAuto> | null = null;

/**
 * Copia automática del presupuesto abierto en este dispositivo: la
 * configuración, el estado del ancla y las acciones. Con `vigilar`, hace la
 * copia sola al montarse y al volver a la app si ya toca (lo usa el ancla de
 * Inicio, que es la pantalla con la que se abre la app).
 */
export function useCopiaAutomatica({ vigilar = false } = {}) {
  const [presupuesto] = useMetadataPref('id');
  const [nombre] = useMetadataPref('budgetName');
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const [configRaw, setConfigRaw] = useLocalPref('copia.auto');
  const [autoUltima, setAutoUltima] = useLocalPref('copia.autoUltima');
  const [fallo, setFallo] = useLocalPref('copia.autoFallo');
  const [ultimaManual] = useSyncedPref('copia-ultima-fecha');
  const [copiando, setCopiando] = useState(false);

  const config = leerConfigCopia(configRaw);
  const enNube = Boolean(cloudFileId);
  const ultima = diaMasReciente(autoUltima, ultimaManual);
  const estado = estadoAncla({
    enNube,
    config,
    ultima,
    fallo: fallo === true,
    hoy: diaLocal(new Date()),
  });

  const ejecutar = useCallback(
    async (
      destino: ConfigCopiaAuto,
      { pedirPermiso, origen }: { pedirPermiso: boolean; origen: string },
    ): Promise<ResultadoAuto> => {
      if (!presupuesto) {
        return 'error';
      }
      if (enCurso) {
        return enCurso;
      }
      setCopiando(true);
      enCurso = (async (): Promise<ResultadoAuto> => {
        try {
          const ahora = new Date();
          // La carpeta va antes de exportar: el permiso se pide en el toque.
          const carpeta =
            destino.destino === 'carpeta'
              ? await leerCarpeta(presupuesto)
              : null;
          if (destino.destino === 'carpeta') {
            if (!carpeta || !(await permisoCarpeta(carpeta, pedirPermiso))) {
              return 'sin-permiso';
            }
          }
          const respuesta = await send('export-budget');
          if (('error' in respuesta && respuesta.error) || !respuesta.data) {
            return 'error';
          }
          const datos = new Blob([respuesta.data as unknown as BlobPart], {
            type: 'application/zip',
          });
          const archivo = nombreArchivoAuto(nombre, ahora);
          if (carpeta) {
            await escribirEnCarpeta(carpeta, archivo, datos);
          } else {
            await guardarCopiaInterna(presupuesto, archivo, datos, ahora);
          }
          return 'hecha';
        } catch {
          return 'error';
        }
      })();
      const resultado = await enCurso;
      enCurso = null;
      setCopiando(false);
      if (resultado === 'hecha') {
        setAutoUltima(diaLocal(new Date()));
        setFallo(false);
        registrarUso('copia_hecha', { origen, resultado: destino.destino });
      } else {
        setFallo(true);
        registrarUso(
          resultado === 'error' ? 'copia_error' : 'copia_no_guardada',
          { origen, resultado },
        );
      }
      return resultado;
    },
    [presupuesto, nombre, setAutoUltima, setFallo],
  );

  /** La primera vez: guarda dónde y cada cuánto, y hace ya la primera. */
  const elegir = useCallback(
    async (nueva: ConfigCopiaAuto): Promise<ResultadoAuto> => {
      if (!presupuesto) {
        return 'error';
      }
      if (nueva.destino === 'carpeta') {
        try {
          await elegirCarpeta(presupuesto);
        } catch {
          // Cerró el selector sin elegir: no cambia nada.
          return 'sin-permiso';
        }
      }
      setConfigRaw(nueva);
      return ejecutar(nueva, { pedirPermiso: true, origen: 'elegir' });
    },
    [presupuesto, setConfigRaw, ejecutar],
  );

  /** «Hacer copia ahora» desde la hoja del ancla. */
  const hacerAhora = useCallback(
    () =>
      ejecutar(config ?? { destino: 'rumbo', frecuencia: 'dia' }, {
        pedirPermiso: true,
        origen: 'ancla',
      }),
    [config, ejecutar],
  );

  const desactivar = useCallback(() => {
    setConfigRaw(undefined);
    setFallo(false);
  }, [setConfigRaw, setFallo]);

  // Copia sola al abrir Inicio y al volver a la app, si ya toca.
  const ultimaRef = useRef(autoUltima);
  ultimaRef.current = autoUltima;
  useEffect(() => {
    if (!vigilar || enNube || !config) {
      return;
    }
    const comprobar = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      if (tocaCopiaAuto(config, ultimaRef.current, diaLocal(new Date()))) {
        void ejecutar(config, { pedirPermiso: false, origen: 'automatica' });
      }
    };
    comprobar();
    document.addEventListener('visibilitychange', comprobar);
    return () => document.removeEventListener('visibilitychange', comprobar);
    // `config` es un objeto nuevo en cada render: se vigila por sus campos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vigilar, enNube, config?.destino, config?.frecuencia, ejecutar]);

  return {
    presupuesto,
    config,
    enNube,
    estado,
    ultima,
    copiando,
    elegir,
    hacerAhora,
    desactivar,
    setConfig: setConfigRaw,
  };
}
